import useAdminDataStore, { selectCurrentGroupData } from "../../../../store/adminData/adminData";
import { useCallback, useEffect, useMemo } from "react";
import { fetchMembers } from "../../../../services/api/members";
import useGlobalStore from "../../../../store/store";

const useMembersData = () => {
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const { membersList: membersListMap, membersLoading } = useGlobalStore(
    (state) => state.members,
  );

  const getMemberById = useCallback(
    (id) => {
      return membersListMap?.[groupData?.id]?.find(
        (member) => member.id === id,
      );
    },
    [membersListMap, groupData?.id],
  );

  const membersByMemberId = useMemo(() => {
    return membersListMap?.[groupData?.id]?.reduce((acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    }, {});
  }, [membersListMap, groupData?.id]);

  const familyMembers = useMemo(() => {
    if (
      !membersListMap?.[groupData?.id] ||
      !Array.isArray(membersListMap[groupData.id])
    ) {
      return {};
    }

    const familyMap = membersListMap[groupData.id].reduce((acc, curr) => {
      if (!curr || typeof curr !== "object") {
        return acc;
      }

      const groupKey = curr.familyMembershipId || curr.membershipNumber;

      if (!groupKey) {
        return acc;
      }

      if (!acc[groupKey]) {
        acc[groupKey] = { adults: {}, children: {} };
      }

      if (curr.membershipNumber && curr.name) {
        acc[groupKey].adults[curr.membershipNumber] = {
          name: curr.name,
          membershipNumber: curr.membershipNumber,
        };
      }

      if (curr.children && Array.isArray(curr.children)) {
        curr.children.forEach((child) => {
          if (child && typeof child === "object" && child?.name) {
            acc[groupKey].children[child.membershipNumber || child?.name] = {
              name: child.name,
              membershipNumber: child?.membershipNumber || "",
            };
          }
        });
      }

      return acc;
    }, {});

    Object.keys(familyMap).forEach((key) => {
      const familyData = familyMap[key];
      if (familyData && typeof familyData === "object") {
        const adultsCount = Object.keys(familyData.adults || {}).length;
        const childrenCount = Object.keys(familyData.children || {}).length;

        if (adultsCount === 0 && childrenCount === 0) {
          delete familyMap[key];
        }

        if (adultsCount === 1 && childrenCount === 0) {
          delete familyMap[key];
        }
      }
    });

    const finalObj = {};
    Object.entries(familyMap).forEach(([famId, famData]) => {
      if (famData && typeof famData === "object") {
        finalObj[famId] = {
          adults: Object.values(famData.adults || {}),
          children: Object.values(famData.children || {}),
        };
      }
    });

    return finalObj;
  }, [membersListMap, groupData?.id]);

  useEffect(() => {
    if (!membersListMap?.[groupData?.id]) {
      fetchMembers(groupData?.id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  return {
    membersList: membersListMap?.[groupData?.id],
    familyMembers,
    membersLoading,
    getMemberById,
    membersByMemberId,
  };
};

export default useMembersData;
